/**
 * Feature flags, read the same way on the server and in the browser.
 *
 * `flag(key)` is on or off for whoever the page is for. A server render reads
 * the flags its caller was given for that request (`services.flags` on the
 * render context), and the page carries them to the browser in
 * `script#c-flags`, so the browser starts from exactly what the server
 * rendered. From then on the browser follows the registry's live stream (see
 * `startLive`): a flag switched on or off reaches the page within a moment,
 * and everything that read it - a route, a region, a button - follows, because
 * in the browser `flag()` is a signal read.
 *
 * A flag nobody defined, or one the caller was never sent, is off.
 */
import { devChecks } from '@crown/framework/dom';
import {
  hasGlobal,
  isBoolean,
  isJsonObject,
  type JsonValue,
  parseJson,
} from '@crown/framework/ir';
import { Signal } from '@crown/signal';

export type FlagValues = Readonly<Record<string, boolean>>;

/** The id of the script that carries a server render's flags to the browser. */
export const FLAGS_SCRIPT_ID = 'c-flags';

/**
 * Where the active server render's flags are found. The framework's server
 * context module sets it (a function answering the render's `services.flags`,
 * `undefined` outside a render), so this package needs no server imports.
 */
export const SERVER_FLAGS = Symbol.for('crown.flags.server');
const STATE = Symbol.for('crown.flags.state');

interface BrowserFlags {
  readonly signal: Signal.State<FlagValues>;
  seeded: boolean;
  warned: Set<string>;
}

const holder: typeof globalThis & {
  [SERVER_FLAGS]?: () => FlagValues | undefined;
  [STATE]?: BrowserFlags;
} = globalThis;

const inBrowser = () => hasGlobal('document');

function sameFlags(a: FlagValues, b: FlagValues): boolean {
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) return false;
  return keys.every((key) => a[key] === b[key]);
}

/** Only boolean values, whatever arrived. */
export function onlyBooleans(value: JsonValue | undefined): FlagValues {
  if (value === undefined || !isJsonObject(value)) return {};
  const picked: [string, boolean][] = [];
  for (const [key, on] of Object.entries(value))
    if (isBoolean(on)) picked.push([key, on]);
  return Object.fromEntries(picked);
}

function browserFlags(): BrowserFlags {
  let state = holder[STATE];
  if (!state) {
    state = {
      signal: new Signal.State<FlagValues>({}, { equals: sameFlags }),
      seeded: false,
      warned: new Set(),
    };
    holder[STATE] = state;
  }
  if (!state.seeded && inBrowser()) {
    state.seeded = true;
    const script = document.getElementById(FLAGS_SCRIPT_ID);
    // A payload that does not parse leaves every flag off until the registry answers.
    const parsed = script?.textContent ? parseJson(script.textContent) : null;
    if (parsed?.ok) state.signal.set(onlyBooleans(parsed.value));
  }
  return state;
}

/** Every flag the caller has: the render's on the server, tracked in the browser. */
export function flags(): FlagValues {
  const server = holder[SERVER_FLAGS]?.();
  if (server) return server;
  return browserFlags().signal.get();
}

/** Whether `key` is on for the caller. A tracked read in the browser. */
export function flag(key: string): boolean {
  const values = flags();
  const value = values[key];
  if (value === undefined && devChecks()) {
    const state = holder[STATE];
    // Said once per key, and only once the browser has heard from the registry, so a
    // page that has not been sent its flags yet does not warn about every one.
    if (
      state?.seeded &&
      !state.warned.has(key) &&
      Object.keys(values).length > 0
    ) {
      state.warned.add(key);
      console.warn(
        `[@crown/flags] "${key}" is not a flag the platform knows; it reads as off.`,
      );
    }
  }
  return value === true;
}

/** Replaces the browser's flags: from the page, a fetch or the live stream. */
export function seedFlags(values: FlagValues): void {
  const state = browserFlags();
  state.seeded = true;
  state.signal.set(onlyBooleans(values));
}

/** The flags as the page carries them to the browser, for a server render to write. */
export function flagsScript(
  values: FlagValues,
  inlineJson: (value: JsonValue) => string,
): string {
  return `<script type="application/json" id="${FLAGS_SCRIPT_ID}">${inlineJson(onlyBooleans(values))}</script>`;
}

/** Forgets the browser's flags; for tests. */
export function resetFlagsForTests(): void {
  delete holder[STATE];
}
